import { ago, HomeCareLeadId, PlacementId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { InlineBanner } from "components/ui/inline-banner";
import { CrmPersona } from "components/ui/persona";
import { SecondaryButton } from "components/ui/secondary-button";
import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { stableQueryOptionsRpc } from "util/requests";
import { trpc, trpcClient } from "../rpc/trpc";

type PotentialDuplicateRow = inferProcedureOutput<
    AppRouter["clientMatch"]["potentialDuplicates"]
>[number];

interface DuplicateRecordProps {
    duplicate: PotentialDuplicateRow;
    recordTypeLabel: string | null;
}

const DuplicateRecord: React.FC<DuplicateRecordProps> = props => {
    const { duplicate, recordTypeLabel } = props;

    const personas = (
        <div className="flex items-center space-x-2">
            {duplicate.bestContactName ? (
                <div className="border-r pr-2 border-gray-400">
                    <CrmPersona size="small" name={duplicate.bestContactName} />
                </div>
            ) : null}
            <CrmPersona size="small" name={duplicate.name ?? ""} />
        </div>
    );

    const href =
        duplicate.recordType === "homeCareLead"
            ? `/home-care-clients/show/${duplicate.id}`
            : `/clients/show/${duplicate.id}`;

    return (
        <li className="space-y-1">
            {duplicate.viewable ? <Link to={href}>{personas}</Link> : personas}
            <div className="space-y-1">
                {recordTypeLabel ? (
                    <p className="text-xs italic text-gray-600">{recordTypeLabel}</p>
                ) : null}
                <p className="text-xs italic text-gray-600">
                    {duplicate.assignedUser
                        ? `Assigned to ${duplicate.assignedUser.preferredName}`
                        : "Unassigned"}
                </p>
                <p className="text-xs italic text-gray-600">
                    Created {ago(duplicate.dateAdded)}
                </p>
            </div>
        </li>
    );
};

type DuplicateRecordBannerProps = (
    | { recordType: "placement"; recordId: PlacementId }
    | { recordType: "homeCareLead"; recordId: HomeCareLeadId }
) & { nameKey: string };

export const DuplicateRecordBanner: React.FC<DuplicateRecordBannerProps> = props => {
    const { nameKey } = props;

    const subject = useMemo(
        () =>
            props.recordType === "placement"
                ? { recordType: "placement" as const, id: props.recordId }
                : { recordType: "homeCareLead" as const, id: props.recordId },
        [props.recordType, props.recordId],
    );

    const trpcContext = trpc.useTRPC();
    const query = useQuery({
        ...trpcContext.clientMatch.potentialDuplicates.queryOptions(subject),
        ...stableQueryOptionsRpc(),
        refetchOnMount: true,
    });

    useEffect(() => {
        const handle0 = setTimeout(() => {
            query.refetch();
        }, 500);
        const handle1 = setTimeout(() => {
            query.refetch();
        }, 3000);
        return () => {
            clearTimeout(handle0);
            clearTimeout(handle1);
        };
    }, [nameKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDismiss = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e && e.preventDefault && e.preventDefault();
            await trpcClient.clientMatch.dismiss.mutate(subject);
            query.refetch();
        },
        [subject, query],
    );

    if (query.isLoading || query.isError || !query.data) {
        return null;
    }

    const hasMixedRecordTypes =
        query.data.some(duplicate => duplicate.recordType === "homeCareLead") &&
        query.data.some(duplicate => duplicate.recordType === "placement");

    return query.data.length > 0 ? (
        <InlineBanner type="warning" showIcon={false} fontStyle="semibold">
            <p>This client record may be a duplicate of one of the clients below.</p>
            <ul className="mt-2 text-sm space-y-2">
                {query.data.map(duplicate => (
                    <DuplicateRecord
                        key={duplicate.id}
                        duplicate={duplicate}
                        recordTypeLabel={
                            hasMixedRecordTypes
                                ? duplicate.recordType === "homeCareLead"
                                    ? "Home care client"
                                    : "Placement client"
                                : null
                        }
                    />
                ))}
            </ul>
            <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                    <SecondaryButton onClick={handleDismiss}>Dismiss</SecondaryButton>
                </div>
            </div>
        </InlineBanner>
    ) : null;
};
